import { useCallback, useState } from "react";
import { toast } from "react-toastify";
import {
  captureFingerprintImage,
  detectFingerprintDevice,
  FINGERPRINT_DEVICE_ERROR,
  FingerprintDevice,
  isFingerprintDataUrl,
} from "../utils/biometric";

interface UseFingerprintCaptureOptions {
  onCaptured?: (fingerprintDataUrl: string) => void;
  onCleared?: () => void;
}

export const useFingerprintCapture = (
  options: UseFingerprintCaptureOptions = {}
) => {
  const { onCaptured, onCleared } = options;
  const [fingerprintDevice, setFingerprintDevice] =
    useState<FingerprintDevice | null>(null);
  const [detectingDevice, setDetectingDevice] = useState(false);
  const [capturingFingerprint, setCapturingFingerprint] = useState(false);
  const [isFingerprintImage, setIsFingerprintImage] = useState(false);

  const detectDevice = useCallback(async (showErrorToast: boolean = true) => {
    setDetectingDevice(true);
    try {
      const device = await detectFingerprintDevice();
      if (device) {
        setFingerprintDevice(device);
      } else {
        setFingerprintDevice(null);
        if (showErrorToast) {
          toast.error(FINGERPRINT_DEVICE_ERROR);
        }
      }
    } catch (error) {
      console.error("Fingerprint device detection failed:", error);
      setFingerprintDevice(null);
      if (showErrorToast) {
        toast.error(FINGERPRINT_DEVICE_ERROR);
      }
    } finally {
      setDetectingDevice(false);
    }
  }, []);

  const captureFingerprint = useCallback(async () => {
    if (!fingerprintDevice) {
      toast.error("Please detect the fingerprint device first");
      return;
    }

    setCapturingFingerprint(true);
    try {
      const fingerprintDataUrl = await captureFingerprintImage();
      setIsFingerprintImage(true);
      onCaptured?.(fingerprintDataUrl);
      toast.success("Fingerprint captured successfully");
    } catch (error) {
      console.error("Fingerprint capture failed:", error);
      toast.error(
        "Unable to capture fingerprint. Ensure the device is connected."
      );
    } finally {
      setCapturingFingerprint(false);
    }
  }, [fingerprintDevice, onCaptured]);

  const clearFingerprint = useCallback(() => {
    setIsFingerprintImage(false);
    onCleared?.();
  }, [onCleared]);

  const syncFingerprintFlag = useCallback((dataUrl?: string | null) => {
    setIsFingerprintImage(isFingerprintDataUrl(dataUrl));
  }, []);

  return {
    fingerprintDevice,
    detectingDevice,
    capturingFingerprint,
    isFingerprintImage,
    setIsFingerprintImage,
    detectDevice,
    captureFingerprint,
    clearFingerprint,
    syncFingerprintFlag,
  };
};
